import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Customer } from '../interfaces';
import { formatPrice } from '../utils/formatPrice';
import DeleteConfirmation from '../components/DeleteConfirmationPopup';
import SuccessNotification from '../components/notifications/SuccessNotification';
import Spinner from '../components/Spinner';
import './Customers.css';

// Data-honest activity dimensions derived from real Customer fields only.
type ActivityKey = 'orders' | 'none' | 'reservation';

const Customers: React.FC = () => {
    const [, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const navigate = useNavigate();
    const emptyGuid = '00000000-0000-0000-0000-000000000000';
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [customerIdToDelete, setCustomerIdToDelete] = useState<string | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [headerTitle, setHeaderTitle] = useState('');
    const [content, setContent] = useState('');
    const [activityF, setActivityF] = useState<ActivityKey | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const hasOrders = (c: Customer) => (c.totalOrderAmount || 0) > 0;
    const hasReservation = (c: Customer) => !!c.lastReservationDate;
    const matchesActivity = (c: Customer) =>
        activityF === null ||
        (activityF === 'orders' && hasOrders(c)) ||
        (activityF === 'none' && !hasOrders(c)) ||
        (activityF === 'reservation' && hasReservation(c));

    // Live search across name + email (unchanged principle)
    const searchedCustomers = customers.filter((customer) =>
    (customer.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Activity chip layer on top of the search results
    const filteredCustomers = searchedCustomers.filter(matchesActivity);

    // Chip counts reflect the current search set (mirrors Orders' status counts)
    const activityChips: { key: ActivityKey; label: string; count: number }[] = [
        { key: 'orders', label: 'With orders', count: searchedCustomers.filter(hasOrders).length },
        { key: 'none', label: 'No orders', count: searchedCustomers.filter((c) => !hasOrders(c)).length },
        { key: 'reservation', label: 'Has reservation', count: searchedCustomers.filter(hasReservation).length },
    ];

    const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / itemsPerPage));
    const paginatedCustomers = filteredCustomers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    // Reset to page 1 when search / filter / page size change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, activityF, itemsPerPage]);

    const handleAddCustomer = () => {
        navigate(`/customers/${emptyGuid}`);
    };

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleDelete = async () => {
        try {
            const id = customerIdToDelete;
            setCustomerIdToDelete(null);
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Customer/delete/` + id, {
                method: 'GET',
                credentials: 'include',
            });
            if (response) {
                setHeaderTitle("Success!");
                setContent("Customer was deleted Successfully!");
                setShowSuccess(true);
                await fetchCustomers();
                setShowSpinner(false);
                setTimeout(() => {
                    setShowSuccess(false);
                }, 3000);
            }
        } catch (error) {
            console.error("Error fetching room types: ", error);
        }
    }


    useEffect(() => {
        const handleResize = () => {
            console.log("onresize: innerWidth: " + window.innerWidth + "innerHeight: " + innerHeight)
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.onresize = handleResize;

        return () => {
            window.onresize = null;
        };
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                setWindowSize({
                    width: window.innerWidth,
                    height: window.innerHeight,
                });
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const updateSize = () => {
            console.log("update size")
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", updateSize);
        window.addEventListener("focus", updateSize);
        document.addEventListener("visibilitychange", updateSize);

        return () => {
            window.removeEventListener("resize", updateSize);
            window.removeEventListener("focus", updateSize);
            document.removeEventListener("visibilitychange", updateSize);
        };
    }, []);

    const fetchCustomers = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Customer/`, {
                method: 'GET',
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setCustomers(data);
        } catch (error) {
            console.error("Error fetching customer data: ", error);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setShowSpinner(true);
            await fetchCustomers();
            setShowSpinner(false);
        };

        fetchData();
    }, []);

    const handleRefresh = async () => {
        setShowSpinner(true);
        await fetchCustomers();
        setShowSpinner(false);
    }

    const formatDate = (date: Date | undefined) => {
        if (!date) return "-";

        const dateObj = new Date(date);
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();

        return `${day}/${month}/${year}`;
    };


    return (
        <div className="customers-view cv2 ps-scope">
            <header className="cv2-head">
                <div className="cv2-head__lead">
                    <h1 className="cv2-title">Customers</h1>
                    <span className="cv2-today">{filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="cv2-head__tools">
                    <button className="cv2-tool" onClick={handleRefresh}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M3 10a9 9 0 0 1 15-3.7L21 9M21 5v4h-4M21 14a9 9 0 0 1-15 3.7L3 15m0 4v-4h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        Refresh
                    </button>
                    <button className="cv2-tool cv2-tool--primary" onClick={handleAddCustomer}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1.167V12.833M1.167 7h11.666" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        Add Customer
                    </button>
                    <div className="cv2-search">
                        <svg width="14" height="14" viewBox="0 0 18 18" fill="none"><path d="M16.5 16.5L13.5834 13.5833M15.6667 8.58333C15.6667 12.4954 12.4954 15.6667 8.58333 15.6667C4.67132 15.6667 1.5 12.4954 1.5 8.58333C1.5 4.67132 4.67132 1.5 8.58333 1.5C12.4954 1.5 15.6667 4.67132 15.6667 8.58333Z" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        <input type="text" placeholder="Search customers" value={searchTerm} onInput={handleSearch} />
                    </div>
                </div>
            </header>

            <div className="cv2-filters">
                <div className="cv2-chips">
                    {activityChips.map((chip) => (
                        <button
                            key={chip.key}
                            className={`cv2-chip${activityF === chip.key ? ' is-active' : ''}`}
                            onClick={() => setActivityF(activityF === chip.key ? null : chip.key)}
                        >
                            {chip.label}<span className="cv2-chip__n">{chip.count}</span>
                        </button>
                    ))}
                    {activityF !== null && <button className="cv2-clear" onClick={() => setActivityF(null)}>Clear</button>}
                </div>
            </div>

            <section className="cv2-tablewrap">
                <div className="cv2-table">
                    <div className="cv2-row cv2-row--head">
                        <span>Customer Name</span>
                        <span className="cv2-r">Total Order Amount</span>
                        <span>Last Reservation</span>
                        <span className="cv2-r">·</span>
                    </div>
                    {paginatedCustomers.map((customer) => (
                        <div key={customer.id} className="cv2-row">
                            <span className="cv2-trunc"><b>{customer.firstName} {customer.lastName}</b></span>
                            <span className="cv2-r cv2-mono">{formatPrice(customer.totalOrderAmount)}</span>
                            <span className="cv2-mono cv2-dim">{formatDate(customer.lastReservationDate)}</span>
                            <span className="cv2-r cv2-actions">
                                <button className="cv2-iconbtn" title="Delete" onClick={() => setCustomerIdToDelete(customer.id)}>
                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M13.3333 4.99935V4.33268C13.3333 3.39926 13.3333 2.93255 13.1517 2.57603C12.9919 2.26243 12.7369 2.00746 12.4233 1.84767C12.0668 1.66602 11.6001 1.66602 10.6667 1.66602H9.33333C8.39991 1.66602 7.9332 1.66602 7.57668 1.84767C7.26308 2.00746 7.00811 2.26243 6.84832 2.57603C6.66667 2.93255 6.66667 3.39926 6.66667 4.33268V4.99935M8.33333 9.58268V13.7493M11.6667 9.58268V13.7493M2.5 4.99935H17.5M15.8333 4.99935V14.3327C15.8333 15.7328 15.8333 16.4329 15.5608 16.9677C15.3212 17.4381 14.9387 17.8205 14.4683 18.0602C13.9335 18.3327 13.2335 18.3327 11.8333 18.3327H8.16667C6.76654 18.3327 6.06647 18.3327 5.53169 18.0602C5.06129 17.8205 4.67883 17.4381 4.43915 16.9677C4.16667 16.4329 4.16667 15.7328 4.16667 14.3327V4.99935" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <button className="cv2-iconbtn" title="Edit" onClick={() => navigate(`/customers/${customer.id}`)}>
                                    <svg width="16" height="16" viewBox="0 0 19 19" fill="none"><path d="M1.39637 15.0973C1.43466 14.7527 1.4538 14.5804 1.50594 14.4194C1.55219 14.2765 1.61753 14.1405 1.70021 14.0152C1.79339 13.8739 1.91596 13.7513 2.16112 13.5061L13.1664 2.5009C14.0868 1.58043 15.5792 1.58043 16.4997 2.5009C17.4202 3.42138 17.4202 4.91376 16.4997 5.83424L5.49445 16.8395C5.2493 17.0846 5.12672 17.2072 4.98541 17.3004C4.86005 17.383 4.72408 17.4484 4.58121 17.4946C4.42018 17.5468 4.24789 17.5659 3.90331 17.6042L1.08301 17.9176L1.39637 15.0973Z" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </span>
                        </div>
                    ))}
                    {filteredCustomers.length === 0 && <div className="cv2-empty">No customers match this filter.</div>}
                </div>

                {filteredCustomers.length > 0 && (
                    <div className="cv2-pager">
                        <span className="cv2-pager__info">
                            {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filteredCustomers.length)} of {filteredCustomers.length}
                        </span>
                        <div className="cv2-pager__ctrls">
                            <select className="cv2-pager__select" value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}>
                                <option value={25}>25 / page</option>
                                <option value={50}>50 / page</option>
                                <option value={100}>100 / page</option>
                            </select>
                            <button className="cv2-pager__btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>&#8249;</button>
                            <span className="cv2-pager__page">{currentPage} / {totalPages}</span>
                            <button className="cv2-pager__btn" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>&#8250;</button>
                        </div>
                    </div>
                )}
            </section>

            {
                customerIdToDelete &&
                <>
                    <div className='popup-overlay'>
                    </div>
                    <DeleteConfirmation title='Delete Confirmation' content='Are you sure that you want to delete this item?' onClose={() => setCustomerIdToDelete(null)} onDelete={handleDelete} />
                </>
            }
            {showSuccess && <SuccessNotification headerTitle={headerTitle} content={content} />}
            {showSpinner && (
                <Spinner />
            )}
        </div>
    );
};

export default Customers;

